import ProfileFollowers, {
  ProfileFollowersType,
} from '@components/profile/ProfileFollowers'
import ProfilePosts from '@components/profile/ProfilePosts'
import { Button } from '@components/ui/button'
import Loader from '@components/ui/loader'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/ui/tabs'
import { useMutation } from '@connectrpc/connect-query'
import { useAuthenticatedMutation, useSession } from '@context/sessions'
import { parseErrorMessage } from '@lib/errors'
import { cn, formatNumber } from '@lib/utils'
import {
  Globe,
  Heart,
  Send,
  StickyNote,
  Tag,
  UserCheck,
  UserPen,
  UserPlus,
  UserRound,
  UserX,
} from 'lucide-react'
import React, { FC, MouseEvent, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { toast } from 'sonner'
import {
  blockUser,
  followUser,
  getUserByUsername,
  unfollowUser,
} from '@gen/api/v1/api-ApiService_connectquery'
import { User } from '@gen/api/v1/api_pb'
import NotFoundPage from './NotFoundPage'
import Title from '@components/title'

const ProfilePage: FC = () => {
  const params = useParams()
  const session = useSession()

  const [followersOpen, setFollowersOpen] = useState(false)
  const [followingOpen, setFollowingOpen] = useState(false)
  const [username, setUsername] = useState(params.username || 'me')
  const [user, setUser] = useState<User>()
  const [userNotFound, setUserNotFound] = useState(false)

  const blockUserMutation = useAuthenticatedMutation(blockUser)
  const followUserMutation = useAuthenticatedMutation(followUser)
  const getUserMutation = useMutation(getUserByUsername)
  const unfollowUserMutation = useAuthenticatedMutation(unfollowUser)

  const handleBlockUser = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    try {
      if (user) {
        await blockUserMutation.mutateAsync({
          id: user.id,
        })
        toast.success('User blocked successfully')
        setUserNotFound(true)
      }
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Failed to block user', {
        description: message,
      })
    }
  }

  const handleFollow = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    try {
      if (user) {
        await followUserMutation.mutateAsync({
          id: user.id,
        })
      }
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Unabled to follow user', {
        description: message,
      })
      return
    }

    try {
      const updatedUser = await getUserMutation.mutateAsync({ username })
      if (updatedUser?.user) {
        setUser(updatedUser.user)
      }
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Unabled to refresh user', {
        description: message,
      })
    }
  }

  const handleUnfollow = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    try {
      if (user) {
        await unfollowUserMutation.mutateAsync({
          id: user.id,
        })
      }
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Unabled to unfollow user', {
        description: message,
      })
      return
    }

    try {
      const updatedUser = await getUserMutation.mutateAsync({ username })
      if (updatedUser?.user) {
        setUser(updatedUser.user)
      }
    } catch (error) {
      const message = parseErrorMessage(error)
      toast.error('Unabled to refresh user', {
        description: message,
      })
    }
  }

  useEffect(() => {
    if (username) {
      if (username === session?.user?.username) {
        setUsername('me')
        return
      }

      if (username === 'me') {
        ;(async () => {
          if (session?.user?.username) {
            setUserNotFound(false)

            const getUserRes = await getUserMutation.mutateAsync({
              username: session.user.username,
            })
            if (getUserRes?.user) {
              setUser(getUserRes.user)
            }
          } else {
            setUserNotFound(true)
          }
        })()
      } else if (username) {
        ;(async () => {
          try {
            setUserNotFound(false)
            const getUserRes = await getUserMutation.mutateAsync({ username })
            if (getUserRes?.user) {
              setUser(getUserRes.user)
            }
          } catch (error) {
            setUserNotFound(true)
          }
        })()
      }
    }
  }, [session, username])

  return (
    <div className="w-full">
      <Title title={`${user?.displayName || user?.username}'s profile`} />
      {userNotFound ? (
        <NotFoundPage />
      ) : (
        <>
          {!user ? (
            <div className="w-full flex items-center justify-center p-12">
              <Loader />
            </div>
          ) : (
            <>
              <div className="w-full flex justify-center mt-8">
                <div className="flex flex-col items-center w-full">
                  {user?.profilePictureUrl ? (
                    <img
                      className="rounded-full w-32 h-32"
                      src={user.profilePictureUrl}
                    />
                  ) : (
                    <div className="rounded-full w-32 h-32 flex items-center justify-center bg-muted text-muted-foreground overflow-hidden">
                      <UserRound className="w-32 h-32" />
                    </div>
                  )}

                  <div className="mt-4 text-center">
                    <p className="text-sm font-semibold">@{user?.username}</p>
                    {user?.displayName && (
                      <p className="mt-1 text-muted-foreground text-xs">
                        {user.displayName}
                      </p>
                    )}

                    <div className="mt-8 grid grid-cols-4 gap-8 w-full">
                      <Sheet
                        open={!!session?.user && followersOpen}
                        onOpenChange={setFollowersOpen}
                      >
                        <SheetTrigger asChild disabled={!session?.user}>
                          <div
                            className={cn(
                              'flex flex-col items-center bg-muted py-2 px48 rounded-lg',
                              session?.user
                                ? 'cursor-pointer'
                                : 'cursor-default',
                            )}
                          >
                            <p className="font-semibold text-sm">
                              {formatNumber(user?.followers)}
                            </p>
                            <p className="font-semibold mt-2 text-xs">
                              followers
                            </p>
                          </div>
                        </SheetTrigger>
                        <SheetContent>
                          <SheetHeader>
                            <SheetTitle className="text-base">
                              Followers{' '}
                              <span className="text-muted-foreground">
                                ({formatNumber(user?.followers)})
                              </span>
                            </SheetTitle>
                          </SheetHeader>

                          <ProfileFollowers
                            setOpen={setFollowersOpen}
                            userId={user.id}
                            type={ProfileFollowersType.Followers}
                          />
                        </SheetContent>
                      </Sheet>
                      <Sheet
                        open={!!session?.user && followingOpen}
                        onOpenChange={setFollowingOpen}
                      >
                        <SheetTrigger asChild disabled={!session?.user}>
                          <div
                            className={cn(
                              'flex flex-col items-center bg-muted py-2 px48 rounded-lg',
                              session?.user
                                ? 'cursor-pointer'
                                : 'cursor-default',
                            )}
                          >
                            <p className="font-semibold text-sm">
                              {formatNumber(user?.following)}
                            </p>
                            <p className="font-semibold mt-2 text-xs">
                              following
                            </p>
                          </div>
                        </SheetTrigger>
                        <SheetContent>
                          <SheetHeader>
                            <SheetTitle className="text-base">
                              Following{' '}
                              <span className="text-muted-foreground">
                                ({formatNumber(user?.following)})
                              </span>
                            </SheetTitle>
                          </SheetHeader>

                          <ProfileFollowers
                            setOpen={setFollowingOpen}
                            userId={user.id}
                            type={ProfileFollowersType.Following}
                          />
                        </SheetContent>
                      </Sheet>
                      <div className="flex flex-col items-center bg-muted py-2 px-4 rounded-lg">
                        <p className="font-semibold text-sm">
                          {formatNumber(user?.posts)}
                        </p>
                        <p className="font-semibold mt-2 text-xs">posts</p>
                      </div>

                      <div className="flex flex-col items-center bg-muted py-2 px-4 rounded-lg">
                        <p className="font-semibold text-sm">
                          {formatNumber(user?.likes)}
                        </p>
                        <p className="font-semibold mt-2 text-xs">likes</p>
                      </div>
                    </div>

                    {user?.bio && <p className="text-sm mt-8">{user.bio}</p>}
                    {user?.website && (
                      <a href={user.website} target="_blank">
                        <div className="inline-block mt-4 text-sm py-2 px-4 bg-muted text-muted-foreground rounded hover:text-foreground hover:bg-secondary transition-colors">
                          <Globe className="h-4 w-4 inline mr-2" />
                          {user.website}
                        </div>
                      </a>
                    )}
                  </div>

                  {username === 'me' ? (
                    <div className="absolute top-8 right-8">
                      <Link to="/settings">
                        <Button className="w-full" variant="outline">
                          <UserPen />
                          Edit Profile
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <>
                      {session?.user && (
                        <div className="mt-8 grid gap-4 grid-cols-3">
                          {user?.followedByMe ? (
                            <Button
                              variant="secondary"
                              onClick={handleUnfollow}
                            >
                              <UserCheck />
                              Following
                            </Button>
                          ) : (
                            <Button
                              className="bg-green-400 hover:bg-green-300 dark:bg-green-400 dark:hover:bg-green-500"
                              onClick={handleFollow}
                            >
                              <UserPlus />
                              Follow
                            </Button>
                          )}
                          <Button variant="secondary">
                            <Send />
                            Message
                          </Button>
                          <Button
                            className="border-destructive text-destructive hover:text-destructive hover:border-2 hover:font-semibold"
                            onClick={handleBlockUser}
                            variant="outline"
                          >
                            <UserX />
                            Block
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="max-w-3xl mx-auto mt-8">
                <Tabs className="w-full mx-auto" defaultValue="posts">
                  <TabsList className="grid max-w-md grid-cols-3 mb-8 mx-auto">
                    <TabsTrigger className="text-sm" value="posts">
                      <StickyNote className="h-4 w-4 mr-2" />
                      Posts
                    </TabsTrigger>
                    <TabsTrigger className="" value="tags">
                      <Tag className="h-4 w-4 mr-2" />
                      Tags
                    </TabsTrigger>
                    <TabsTrigger className="" value="likes">
                      <Heart className="h-4 w-4 mr-2" />
                      Likes
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="posts">
                    {user?.id && <ProfilePosts userId={user.id} />}
                  </TabsContent>
                  <TabsContent value="tags">
                    {user?.id && <ProfilePosts userId={user.id} />}
                  </TabsContent>
                  <TabsContent value="likes">
                    {user?.id && <ProfilePosts userId={user.id} />}
                  </TabsContent>
                </Tabs>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

export default ProfilePage
